import {Component, Input, Output, EventEmitter, forwardRef, inject, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule} from '@angular/forms';
import {CommonModule, DatePipe} from '@angular/common';
import {DatePicker, DatePickerModule} from 'primeng/datepicker';
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule, TranslateModule],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],

  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ]
})
export class DatePickerComponent implements ControlValueAccessor, OnChanges {

  value: Date | Date[] | null = null;
  private lastValidValue: Date | Date[] | null = null;
  private rawValue: any = null; // Store the raw value

  private onTouchedCallback: () => void = () => {
  };
  private onChangeCallback: (_: any) => void = () => {
  };
  datePipe = inject(DatePipe);

  @Input() placeholder: string = 'Select date';
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() showIcon: boolean = true;
  /** Render as a compact calendar icon button (no visible text input); one click opens the calendar. */
  @Input() iconOnly: boolean = false;
  /** Hide the visible input; open the overlay programmatically via {@link open}. */
  @Input() hideTrigger: boolean = false;
  @Input() dateFormat: string = 'dd.mm.yy';
  /**
   * Если true (по умолчанию) — пользователь не сможет выбрать дату,
   * которой нет в selectableDates. Если false — selectableDates используются
   * только для подсветки/валидации снаружи, но выбор не блокируется.
   */
  @Input() restrictToSelectableDates: boolean = true;
  @Input() allowOnlySelectedDate: boolean = false;
  @Input() selectableDates: (String | Date)[] | null = null;

  @Input() showTime: boolean = false;
  @Input() showClear: boolean = false;

  @Input() firstDayOfWeek: number = 1;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() hourFormat: '12' | '24' = '24';
  @Input() timeOnly: boolean = false;
  @Input() dateOnly: boolean = false;
  @Input() showSeconds: boolean = false;
  @Input() selectionMode: 'single' | 'multiple' | 'range' = 'single';
  @Input() numberOfMonths: number = 1;
  @Input() yearOnly: boolean = false;
  @Input() monthOnly: boolean = false;
  @Input() dayOnly: boolean = false;
  @Input() monthAndDayOnly: boolean = false;

  @Output() onSelect = new EventEmitter<Date>();
  @Output() onYearChange = new EventEmitter<number>();
  @Output() onMonthChange = new EventEmitter<number>();

  @ViewChild(DatePicker) private pDatePicker?: DatePicker;

  /** Programmatically open/close the calendar overlay (used by an external icon button). */
  open(): void {
    setTimeout(() => this.pDatePicker?.toggle());
  }



  writeValue(value: any): void {
    this.rawValue = value; // Store raw value
    this.convertValueToDate();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private normalizeRangeValue(value: Date[]): Date[] | null {
    const dates = value.filter((entry): entry is Date => entry instanceof Date);
    if (dates.length === 0) {
      return null;
    }
    if (dates.length === 1) {
      if (!this.isDateSelectable(dates[0])) {
        return this.lastValidValue as Date[] | null;
      }
      return dates;
    }

    const [first, second] = dates;
    if (!this.isDateSelectable(first) || !this.isDateSelectable(second)) {
      return this.lastValidValue as Date[] | null;
    }

    return first.getTime() <= second.getTime() ? [first, second] : [second, first];
  }

  onChange(value: Date | Date[] | null): void {
    if (Array.isArray(value)) {
      if (this.selectionMode === 'range') {
        const normalized = this.normalizeRangeValue(value);
        this.value = normalized;
        this.lastValidValue = normalized;
        this.onChangeCallback(normalized);
        return;
      }

      this.value = value;
      this.lastValidValue = value;
      this.onChangeCallback(value);
      return;
    }

    if (value instanceof Date && !this.isDateSelectable(value)) {
      this.value = this.lastValidValue;
      return;
    }

    this.value = value;
    this.lastValidValue = value;
    let emittedValue: any = value;

    // Handle different date modes
    if (value instanceof Date) {
      if (this.dateOnly) {
        emittedValue = this.datePipe.transform(value, 'yyyy-MM-dd') as any;
      } else if (this.yearOnly) {
        emittedValue = value.getFullYear();
      } else if (this.monthOnly) {
        emittedValue = value.getMonth() + 1; // Returns numeric month (1-12)
      } else if (this.dayOnly) {
        emittedValue = value.getDate(); // Returns numeric day (1-31)
      } else if (this.monthAndDayOnly) {
        // For monthAndDayOnly, we keep the full date but format it as MM-dd
        emittedValue = value;
      }
    }

    this.onChangeCallback(emittedValue);

    if (value instanceof Date) {
      this.onSelect.emit(emittedValue as any);
    }

  }

  onBlur(): void {
    this.onTouchedCallback();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When monthOnly, dayOnly, yearOnly, or monthAndDayOnly changes, reconvert the value
    if (changes['monthOnly'] || changes['dayOnly'] || changes['yearOnly'] || changes['monthAndDayOnly']) {
      this.convertValueToDate();
    }
  }

  private convertValueToDate(): void {
    const value = this.rawValue;

    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        this.value = value
          .filter((entry): entry is Date => entry instanceof Date)
          .slice(0, this.selectionMode === 'range' ? 2 : value.length);
        this.lastValidValue = this.value;
        return;
      }

      // Handle numeric values for monthOnly and dayOnly modes
      if (this.monthOnly && typeof value === 'number') {
        // Create a date with the specific month (value is 1-12)
        const currentYear = new Date().getFullYear();
        this.value = new Date(currentYear, value - 1, 1);
      } else if (this.dayOnly && typeof value === 'number') {
        // Create a date with the specific day (value is 1-31)
        const currentDate = new Date();
        this.value = new Date(currentDate.getFullYear(), currentDate.getMonth(), value);
      } else if (this.yearOnly && typeof value === 'number') {
        // Create a date with the specific year
        this.value = new Date(value, 0, 1);
      } else if (value instanceof Date) {
        this.value = value;
      } else {
        try {
          this.value = new Date(value);
        } catch (e) {
          this.value = null;
        }
      }
    } else {
      this.value = null;
    }
    this.lastValidValue = this.value;
  }

  private isDateSelectable(date: Date): boolean {
    // Если не хотим ограничивать выбор дат — всегда разрешаем
    if (!this.restrictToSelectableDates) {
      return true;
    }

    if (this.allowOnlySelectedDate){
      return true;
    }

    if (!this.selectableDates || this.selectableDates.length === 0) {
      return true;
    }

    return this.selectableDates.some((availableDate) =>
      (availableDate as Date).getFullYear() === date.getFullYear() &&
      (availableDate as Date).getMonth() === date.getMonth() &&
      (availableDate as Date).getDate() === date.getDate()
    );
  }

  yearChange(event: any) {
    this.onYearChange.emit(event.year);
    this.onMonthChange.emit(event.month);
    // Your logic here
  }

  monthChange(event: any) {
    this.onYearChange.emit(event.year);
    this.onMonthChange.emit(event.month);
  }

  isAllowed(date: any): boolean {
    const d = `${date.year}-${String(date.month + 1).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;

    if (!this.selectableDates || !this.allowOnlySelectedDate) {
      return true;
    }


    return (this.selectableDates as string[]).includes(d);
  }

  onDateCellClick(event: Event, date: any): void {
    if (!this.isAllowed(date)) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}
